<script setup>
import { ref, shallowRef, computed, onMounted, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import emojiGroups from 'shared/components/emoji/emojisGroup.json';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectEmoji', 'close']);

const allEmojis = shallowRef([]);
const activeCategory = ref(0);
const pickerRef = ref(null);

// 加载表情数据
const loadEmojis = () => {
  allEmojis.value = emojiGroups.map(group => ({
    ...group,
    emojis: group.emojis.map(emoji => ({
      ...emoji,
      searchString:
        `${emoji.name.replace(/\s+/g, '')} ${emoji.slug}`.toLowerCase(),
    })),
  }));
};

// 当前分类的表情
const currentEmojis = computed(() => {
  if (allEmojis.value.length === 0) return [];
  return allEmojis.value[activeCategory.value]?.emojis || [];
});

// 分类列表
const categories = computed(() => {
  return allEmojis.value.map(group => ({
    name: group.name,
    emoji: group.emojis[0]?.emoji || '😀',
  }));
});

// 选择表情
const handleSelectEmoji = emoji => {
  emit('selectEmoji', emoji);
};

// 切换分类
const setActiveCategory = index => {
  activeCategory.value = index;
};

// 点击外部关闭
const handleClickOutside = () => {
  emit('close');
};

// 监听 isOpen 变化，重置分类
watch(
  () => props.isOpen,
  newVal => {
    if (newVal) {
      activeCategory.value = 0;
    }
  }
);

onMounted(() => {
  loadEmojis();
});
</script>

<template>
  <div
    v-if="isOpen"
    ref="pickerRef"
    v-on-click-outside="handleClickOutside"
    class="absolute bottom-full left-0 mb-2 z-50 w-80 bg-n-background dark:bg-n-slate-900 rounded-xl shadow-lg border border-n-weak dark:border-n-weak overflow-hidden"
  >
    <!-- 分类标签栏 -->
    <div
      class="flex items-center gap-1 px-2 py-2 border-b border-n-weak dark:border-n-weak bg-n-slate-1 dark:bg-n-slate-800 overflow-x-auto"
    >
      <button
        v-for="(category, index) in categories"
        :key="index"
        type="button"
        class="flex-shrink-0 p-1.5 rounded-lg transition-colors hover:bg-n-slate-3 dark:hover:bg-n-slate-700"
        :class="{
          'bg-n-brand/10': activeCategory === index,
        }"
        :title="category.name"
        @click="setActiveCategory(index)"
      >
        <span class="text-lg">{{ category.emoji }}</span>
      </button>
    </div>

    <!-- 表情网格 -->
    <div class="p-3 max-h-64 overflow-y-auto custom-scrollbar">
      <div class="grid grid-cols-8 gap-1">
        <button
          v-for="emoji in currentEmojis"
          :key="emoji.slug"
          type="button"
          class="p-1 rounded hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors flex items-center justify-center text-xl cursor-pointer"
          :title="emoji.name"
          @click="handleSelectEmoji(emoji.emoji)"
        >
          {{ emoji.emoji }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
